import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import type { Theme as MuiV4Theme } from "@material-ui/core/styles";
import { useTheme, type Theme } from "@mui/material/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Typography from "@material-ui/core/Typography";
import {
  selectAreAllPagesFetched,
  selectGetProductIds,
  selectGetProductListForFindProductsModal,
  selectIsFetchingProductIds,
  selectIsProductIdsFetched,
  selectProductsColorDataForFindProducts,
  selectDisplayProductForFindProductsModal,
} from "../../store/find-products/FindProductSelectors";
import { ProductSequenceData } from "../../store/product-list/ProductListTypes";
import { CircularProgress, Skeleton } from "@mui/material";
import styled from "styled-components";
import { useIntl } from "react-intl";
import {
  selectChildCategories,
  selectTopCategories,
  selectCurrentCategory,
} from "../../store/category/CategorySelectors";
import { selectCurrentStoreId } from "../../store/store-list/StoreListSelectors";
import { selectCurrentCatalogId } from "../../store/catalog/CatalogSelectors";
import {
  updateProductAddCategoryRequestAction,
  updateProductRemoveCategoryRequestAction,
} from "../../store/product/ProductActions";
import {
  unLockProduct,
  setPinnedProducts,
} from "../../store/product-list/ProductListActions";
import {
  selectLockedProducts,
  selectPinnedProductIds,
} from "../../store/product-list/ProductListSelectors";
import { selectAdapterInfo } from "store/adapter-config/AdapterConfigSelectors";
import { AdapterInfoObject } from "utils/AppConfigUtils";
import CheckboxComponent from "../common/Checkbox";
import { NoSearchResultIcon } from "../../assets";
import {
  selectProductImagePrefix,
  selectConfigValue,
} from "store/app-config/AppConfigSelectors";
import AppState from "store/AppState";
import {
  selectAllProductsFetched,
  selectProductFetched,
  selectIsProductDetailRefereshing,
  selectIsProductColorDetailRefereshing,
} from "store/product/ProductSelectors";
import { isRefreshingAllProducts } from "store/product-list/ProductListSelectors";
import { Product } from "store/product/ProductTypes";
import ProductColorSwatchStrip, {
  colorMatchesProductTileStripRow,
} from "components/common/ProductColorSwatchStrip";
import { ColorData } from "store/color-management/ColorManagementTypes";
import { PLATFORM_DEFAULTS } from "utils/DefaultConfigs";
import FindProductsTileHoverActions, {
  FIND_PRODUCTS_TILE_HOVER_ACTIONS_CLASS,
} from "./FindProductsTileHoverActions";
import ProductTileContent from "../product-title-content/ProductTitleContent";
import {
  ViewContext,
  createStaticViewContextValue,
} from "../../context/view-context";
import { LoadingOverlay } from "components/common/LoadingOverlay";

/** Always use main-grid 4-up typography/size tokens in Find Products (independent of global View). */
const FindProductsFourColumnView = ViewContext.Provider;

/** Match `paper` height; MUI `Skeleton` needs explicit `height` prop (JSS class height is ignored). */
const FIND_PRODUCTS_TILE_HEIGHT_PX = 440;

function computeFindProductsTile(
  product: Product | undefined,
  inventoryThreshold: number,
  theme: Theme,
): {
  paperBg?: string;
  inventoryStripeColor: string | null;
} {
  if (!product) {
    return { inventoryStripeColor: null };
  }
  let inventoryStripeColor: string | null = null;
  const productInventory = product.stock;
  if (productInventory === 0) inventoryStripeColor = "#D50000";
  else if (productInventory != null && inventoryThreshold) {
    if (productInventory <= inventoryThreshold)
      inventoryStripeColor = "#F8A510";
  }
  const isDraft = product.isDraft ?? false;
  const isArchived = product.isArchived ?? false;
  const isUnlisted = product.isUnlisted ?? false;
  const isPublished = product.isPublished ?? false;
  const mutedTile = isDraft || isArchived || isUnlisted || !isPublished;

  return {
    paperBg: mutedTile
      ? theme.palette.neutral.lightGrey
      : theme.palette.tableHeaderRow.contrastText,
    inventoryStripeColor,
  };
}

type FindProductsCardStyleProps = {
  paperBg?: string;
  stripeHex?: string | null;
};

const useFindProductsCardStyles = makeStyles<
  MuiV4Theme,
  FindProductsCardStyleProps
>({
  paperTint: (props) =>
    props.paperBg != null
      ? {
          backgroundColor: props.paperBg,
          boxSizing: "border-box",
        }
      : {},
  inventoryStripeBase: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 13,
    zIndex: 1,
    pointerEvents: "none",
  },
  inventoryStripeFill: (props) => ({
    backgroundColor: props.stripeHex ?? "transparent",
  }),
  refreshOverlay: {
    zIndex: 25,
  },
});

const useStyles = makeStyles(() => ({
  formControl: {
    height: "90%",
    overflow: "auto",
    padding: "1rem",
  },
  paper: {
    height: FIND_PRODUCTS_TILE_HEIGHT_PX,
    display: "flex",
    flexDirection: "column",
    position: "relative",
    [`&:hover .${FIND_PRODUCTS_TILE_HOVER_ACTIONS_CLASS}`]: {
      opacity: 1,
      pointerEvents: "auto",
    },
  },
  tileInner: {
    width: "100%",
    overflow: "hidden",
    margin: 0,
    padding: "0.85rem",
    position: "relative",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  tileFontScope: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    width: "100%",
  },
  tileFontScope4Up: {
    fontSize: ".75rem",
  },
  tileBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    width: "100%",
    alignItems: "stretch",
    alignSelf: "stretch",
    textAlign: "left",
    overflow: "hidden",
  },
  tileProductBlock: {
    width: "100%",
    minWidth: 0,
  },
  checkBox: {
    margin: "5px",
    padding: "5px 8px",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxSizing: "border-box" as const,
  },
  swatchRow: {
    minHeight: 48,
    flexShrink: 0,
  },
  circularProgress: {
    marginRight: "8px",
  },
  categoryLabel: {
    fontSize: "0.75rem",
  },
  noResultDiv: { textAlign: "center", width: "100%" },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  skeletonsContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    rowGap: "10px",
  },
  skeletonSlot: {
    width: "33%",
    height: 440,
  },
}));

interface Props {
  currentModalSelectedCategory: string | null;
  productsToBeAdded: {};
  setProductsToBeAdded: React.Dispatch<React.SetStateAction<{}>>;
  productsToBeRemoved: {};
  setProductsToBeRemoved: React.Dispatch<React.SetStateAction<{}>>;
  existingAddingRemoveProductIdsOperation?: string[];
}

const StyledProductList = styled.div`
  height: 100%;
  margin-top: 10px;
  width: 100%;
`;

const FindProductsProductCard: React.FC<{
  item: ProductSequenceData;
  tileIndex: number;
  isLoadingState: boolean;
  inventoryThreshold: number;
  findProductsModalCategoryId: string;
  renderLabel: (
    item: ProductSequenceData,
    isLoading: boolean,
    product: Product | undefined,
    preferMainProductColors: boolean,
    tileIndex: number,
  ) => React.ReactNode;
  paperClass: string;
  tileInnerClass: string;
}> = ({
  item,
  tileIndex,
  isLoadingState,
  inventoryThreshold,
  findProductsModalCategoryId,
  renderLabel,
  paperClass,
  tileInnerClass,
}) => {
  const theme = useTheme();
  const productId = item.productId;
  const productFromMain = useSelector((state: AppState) =>
    selectProductFetched(state, productId),
  );
  const displayProduct = useSelector((state: AppState) =>
    selectDisplayProductForFindProductsModal(state, productId),
  );
  const preferMainProductColors = productFromMain != null;

  const isProductDetailRefreshing = useSelector((state: AppState) =>
    selectIsProductDetailRefereshing(state, productId),
  );
  const isProductColorDetailRefreshing = useSelector((state: AppState) =>
    selectIsProductColorDetailRefereshing(state, productId),
  );
  const isRefreshingEveryProduct = useSelector(isRefreshingAllProducts);

  const showRefreshOverlay =
    isProductDetailRefreshing ||
    isProductColorDetailRefreshing ||
    isRefreshingEveryProduct;

  const { paperBg, inventoryStripeColor } = computeFindProductsTile(
    displayProduct,
    inventoryThreshold,
    theme,
  );
  const cardClasses = useFindProductsCardStyles({
    paperBg,
    stripeHex: inventoryStripeColor,
  });

  return (
    <Grid item sm={3}>
      <Paper className={clsx(paperClass, cardClasses.paperTint)}>
        {inventoryStripeColor ? (
          <div
            aria-hidden
            className={clsx(
              cardClasses.inventoryStripeBase,
              cardClasses.inventoryStripeFill,
            )}
          />
        ) : null}
        {showRefreshOverlay ? (
          <LoadingOverlay className={cardClasses.refreshOverlay}>
            <CircularProgress size={40} />
          </LoadingOverlay>
        ) : null}
        <FindProductsTileHoverActions
          productId={productId}
          product={displayProduct}
          findProductsModalCategoryId={findProductsModalCategoryId}
        />
        <div className={tileInnerClass}>
          {renderLabel(
            item,
            isLoadingState,
            displayProduct,
            preferMainProductColors,
            tileIndex,
          )}
        </div>
      </Paper>
    </Grid>
  );
};

const productListLoading = (count: number) => {
  if (count <= 0) return null;
  return (
    <Grid container spacing={2} direction="row">
      {[...Array(count)].map((_, i) => (
        <Grid item sm={3} key={i}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={FIND_PRODUCTS_TILE_HEIGHT_PX}
          />
        </Grid>
      ))}
    </Grid>
  );
};

const FindProductsProductList: React.FC<Props> = (props) => {
  const classes = useStyles();
  const intl = useIntl();
  const {
    currentModalSelectedCategory,
    existingAddingRemoveProductIdsOperation,
  } = props;
  const productList = useSelector(selectGetProductListForFindProductsModal);
  const isAllPagesFetched = useSelector(selectAreAllPagesFetched);
  const productIds = useSelector(selectGetProductIds);
  const isFetchingProductIds = useSelector(selectIsFetchingProductIds);
  const isProductIdsFetched = useSelector(selectIsProductIdsFetched);
  const allProducts = useSelector(selectAllProductsFetched);

  const findProductsFourColumnViewValue = useMemo(
    () => createStaticViewContextValue(4),
    [],
  );

  const topCategoriesMainSequence = useSelector(selectTopCategories);
  const childCategoriesMainSequence = useSelector(selectChildCategories);

  const currentStoreId = useSelector(selectCurrentStoreId);
  const currentCategoryId = useSelector(selectCurrentCategory);
  const catalogId = useSelector(selectCurrentCatalogId);
  const adapterInfo: AdapterInfoObject = useSelector((state: AppState) =>
    selectAdapterInfo(state),
  );
  const unmanagedSupported = adapterInfo?.capabilities?.includes(
    "unmanaged-products",
  )
    ? "unmanaged"
    : "manual";

  const [checkedProductIds, setCheckedProductIds] = useState([] as string[]);
  const [swatchSelections, setSwatchSelections] = useState<
    Record<string, string>
  >({});

  const productsColorData = useSelector(selectProductsColorDataForFindProducts);
  const productImagePrefix = useSelector((state: AppState) =>
    selectProductImagePrefix(state, currentStoreId),
  );
  const ecomPlatformType = adapterInfo?.adapterId?.toUpperCase() ?? "";
  const platformDefaults = PLATFORM_DEFAULTS[ecomPlatformType];
  const storeAndAccountInventory = useSelector((state: AppState) =>
    selectConfigValue(state, "inventoryThreshold"),
  );
  const inventoryThreshold = parseInt(
    storeAndAccountInventory !== ""
      ? storeAndAccountInventory
      : String(platformDefaults?.inventoryThreshold ?? ""),
    10,
  );

  const pinnedProductIds = useSelector(selectPinnedProductIds);
  const lockedProductsInCategory = useSelector((state: AppState) =>
    selectLockedProducts(state, currentCategoryId, catalogId),
  );

  const dispatch = useDispatch();

  // Check if products are in the current category (from sequence tab)
  useEffect(() => {
    if (
      productList.length &&
      currentCategoryId &&
      Object.keys(allProducts).length > 0
    ) {
      const currentCategoryProductIdsSet = new Set(Object.keys(allProducts));
      const productIdsInCurrentCategory = productList
        .filter((product) =>
          currentCategoryProductIdsSet.has(product.productId),
        )
        .map((product) => product.productId);
      setCheckedProductIds(productIdsInCurrentCategory);
    } else if (!productList.length || !currentCategoryId) {
      // Reset when productList is empty or no current category
      setCheckedProductIds([]);
    }
  }, [productList, currentCategoryId, allProducts]);

  useEffect(() => {
    if (productList.length === 0) {
      setSwatchSelections({});
    }
  }, [productList.length]);

  const addRemoveFromCurrentCategory = useCallback(
    (productId: string | undefined, currentCategoryId: string, e: any) => {
      const tc = topCategoriesMainSequence.find(
        (e) => e.categoryId === currentCategoryId,
      );
      const ccFilter = Object.entries(childCategoriesMainSequence).find((e) =>
        e[1].childCategories.find((el) => el.categoryId === currentCategoryId),
      );
      const payload = {
        sequence: 0,
        productId,
        categoryId: currentCategoryId,
        storeId: currentStoreId,
        parentId: !tc && ccFilter ? ccFilter[0] : null,
        isFromFindProductsModal: true,
      };
      if (e.target.checked) {
        dispatch(updateProductAddCategoryRequestAction(payload));
      } else {
        // When unchecking, check if product has locks or pins and remove them
        if (productId) {
          // Check if product is locked in the category
          const lockedProduct = lockedProductsInCategory?.find(
            (lock) => lock.productId === productId,
          );

          // Check if product is pinned
          const isPinned = pinnedProductIds?.includes(productId);

          // Remove lock if product is locked
          if (lockedProduct) {
            dispatch(
              unLockProduct({
                unlockedProducts: [lockedProduct],
              }),
            );
          }

          // Remove pin if product is pinned
          if (isPinned && pinnedProductIds) {
            const updatedPinnedProducts = pinnedProductIds.filter(
              (id) => id !== productId,
            );
            dispatch(
              setPinnedProducts({
                pinnedProductIds: updatedPinnedProducts,
                sortOption: unmanagedSupported,
                catalogId: catalogId,
                categoryId: currentCategoryId,
              }),
            );
          }
        }

        dispatch(updateProductRemoveCategoryRequestAction(payload));
      }
    },
    [
      topCategoriesMainSequence,
      childCategoriesMainSequence,
      currentStoreId,
      catalogId,
      unmanagedSupported,
      lockedProductsInCategory,
      pinnedProductIds,
      dispatch,
    ],
  );

  const renderLabel = useCallback(
    (
      item: ProductSequenceData,
      isLoading: boolean,
      product: Product | undefined,
      preferMainProductColors: boolean,
      tileIndex: number,
    ) => {
      const productId = item.productId;
      const productThumbnail = product?.thumbnail;
      const findModalColors = productsColorData[productId]?.colors ?? [];
      const entityColors = product?.colors ?? [];
      const mergedColors: ColorData[] = preferMainProductColors
        ? entityColors.length > 0
          ? entityColors
          : findModalColors
        : findModalColors.length > 0
          ? findModalColors
          : entityColors;
      const publishedColors = mergedColors.filter((c) => c.isPublished);
      const stripEligibleColors = publishedColors.filter(
        colorMatchesProductTileStripRow,
      );

      const pickedName = swatchSelections[productId];
      let selectedColorName = "";
      if (stripEligibleColors.length > 0) {
        if (
          pickedName &&
          stripEligibleColors.some((c) => c.name === pickedName)
        ) {
          selectedColorName = pickedName;
        } else if (!pickedName) {
          // Default to first published color shown in the strip (order matches merged list).
          selectedColorName = stripEligibleColors[0]?.name ?? "";
        } else {
          selectedColorName = stripEligibleColors[0]?.name ?? "";
        }
      }

      const selectedColor = stripEligibleColors.find(
        (c) => c.name === selectedColorName,
      );
      const fromSelectedColor =
        selectedColorName && selectedColor
          ? selectedColor.thumbnail?.trim()
            ? selectedColor.thumbnail
            : undefined
          : undefined;
      const displayThumbRaw = fromSelectedColor ?? productThumbnail;

      const hasColorsForStrip = Boolean(
        product?.hasColors && stripEligibleColors.length > 0,
      );

      return (
        <div
          className={clsx(classes.tileFontScope, classes.tileFontScope4Up)}
        >
          <div className={classes.tileBody}>
            {product ? (
              <div className={classes.tileProductBlock}>
                <FindProductsFourColumnView
                  value={findProductsFourColumnViewValue}
                >
                  <ProductTileContent
                    product={product}
                    index={tileIndex}
                    productView={false}
                    isDragging={false}
                    findProductsModal
                    thumbnailPathOverride={displayThumbRaw}
                    rating={-1}
                    reviewCount={-1}
                  />
                </FindProductsFourColumnView>
              </div>
            ) : null}
            {currentCategoryId ? (
              isLoading ? (
                <FormControlLabel
                  className={classes.checkBox}
                  control={
                    <CircularProgress
                      size={20}
                      className={classes.circularProgress}
                    />
                  }
                  label={<p className={classes.categoryLabel}>In Category</p>}
                />
              ) : (
                <FormControlLabel
                  className={classes.checkBox}
                  control={
                    <CheckboxComponent
                      checkboxId="FindProductsProductList"
                      checked={
                        checkedProductIds.findIndex(
                          (id) => id === productId,
                        ) !== -1
                      }
                      value={productId}
                      setCheckedProductIds={setCheckedProductIds}
                      currentCategoryId={currentCategoryId || ""}
                      addRemoveFromCurrentCategory={
                        addRemoveFromCurrentCategory
                      }
                      handleParam={productId}
                    />
                  }
                  label={<p className={classes.categoryLabel}>In Category</p>}
                />
              )
            ) : null}
          </div>
          <div
            className={classes.swatchRow}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <ProductColorSwatchStrip
              productId={productId}
              publishedColors={publishedColors}
              typeCode={product?.typeCode}
              hasColors={hasColorsForStrip}
              selectedColorName={selectedColorName}
              onColorChange={(_thumb, name, _colorId) => {
                setSwatchSelections((s) => ({ ...s, [productId]: name }));
              }}
              productImagePrefix={productImagePrefix}
              inventoryThreshold={inventoryThreshold}
              maxVisible={4}
            />
          </div>
        </div>
      );
    },
    [
      findProductsFourColumnViewValue,
      productsColorData,
      swatchSelections,
      productImagePrefix,
      inventoryThreshold,
      classes.tileBody,
      classes.tileFontScope,
      classes.tileFontScope4Up,
      classes.tileProductBlock,
      classes.checkBox,
      classes.swatchRow,
      classes.circularProgress,
      classes.categoryLabel,
      checkedProductIds,
      currentCategoryId,
      addRemoveFromCurrentCategory,
    ],
  );
  return (
    <StyledProductList>
      <Grid container spacing={2} direction="row">
        {productList &&
          productList.length > 0 &&
          productList.map((item, index) => {
            const isLoadingState =
              existingAddingRemoveProductIdsOperation?.length
                ? existingAddingRemoveProductIdsOperation.findIndex(
                    (id) => id === item.productId,
                  ) !== -1
                : false;

            return (
              <FindProductsProductCard
                key={`FindProductsProductsList_${index}`}
                item={item}
                tileIndex={index}
                isLoadingState={isLoadingState}
                inventoryThreshold={inventoryThreshold}
                findProductsModalCategoryId={
                  currentModalSelectedCategory ?? ""
                }
                renderLabel={renderLabel}
                paperClass={classes.paper}
                tileInnerClass={classes.tileInner}
              />
            );
          })}
      </Grid>
      {!isAllPagesFetched
        ? productListLoading(
            productList.length === 0
              ? productIds.length
              : productIds.length - productList.length,
          )
        : null}
      {isAllPagesFetched &&
        productList.length === 0 &&
        (currentModalSelectedCategory !== "" || isProductIdsFetched) && (
          <div className={classes.noResultDiv}>
            {/* // <Box position="absolute" top="50%" left="45%"> */}
            <NoSearchResultIcon />
            <Typography variant="h5">
              {intl.formatMessage({
                id: "productListSwitch.noProductsFoundText",
                defaultMessage: "No Products Available",
              })}
            </Typography>
          </div>
          //  </Box>
        )}
      {productIds.length === 0 &&
        ((currentModalSelectedCategory !== "" && !isAllPagesFetched) ||
          isFetchingProductIds) && (
          <CircularProgress size={24} className={classes.buttonProgress} />
        )}
    </StyledProductList>
  );
};

export default FindProductsProductList;
